/**
 * DbMigrationPanel — Super-Admin server-to-server DB migration.
 *
 * Paste the target cluster's MONGO_URL + DB name (stored encrypted), then
 * run a WIPE-and-COPY migration source → target. Progress polls the backend.
 * Used to leave a managed host for a self-managed cluster.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Database, Loader2, AlertTriangle, CheckCircle2, XCircle, Save } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DbMigrationPanel() {
  const { authToken } = useAuth();
  const auth = { Authorization: `Bearer ${authToken}` };

  const [mongoUrl, setMongoUrl] = useState('');
  const [dbName, setDbName] = useState('');
  const [target, setTarget] = useState(null);     // {configured, db_name, masked_url}
  const [saving, setSaving] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  const armed = typed.trim() === 'MIGRATE';
  const running = job?.state === 'running';

  // ── Load target config + any in-flight/last job ──────────────────────────
  const loadTarget = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate/target`, { headers: auth });
      if (res.ok) {
        const data = await res.json();
        setTarget(data);
        if (data.db_name) setDbName(data.db_name);
      }
    } catch { /* ignore */ }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate/status`, { headers: auth });
      if (res.ok) {
        const data = await res.json();
        setJob(data.state === 'none' ? null : data);
        return data;
      }
    } catch { /* ignore */ }
    return null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => { loadTarget(); loadStatus(); }, [loadTarget, loadStatus]);

  // ── Poll while running ───────────────────────────────────────────────────
  useEffect(() => {
    if (running) {
      pollRef.current = setInterval(async () => {
        const data = await loadStatus();
        if (data && data.state !== 'running') clearInterval(pollRef.current);
      }, 2000);
      return () => clearInterval(pollRef.current);
    }
  }, [running, loadStatus]);

  const handleSaveTarget = async () => {
    if (!mongoUrl.trim() || !dbName.trim()) {
      toast.error('Enter both the target MONGO_URL and DB name.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate/target`, {
        method: 'PUT',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ mongo_url: mongoUrl.trim(), db_name: dbName.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Save failed'); return; }
      toast.success('Target saved (encrypted)');
      setMongoUrl('');               // never keep the secret in component state
      await loadTarget();
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleRun = async () => {
    if (!armed || starting) return;
    setStarting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate/run`, {
        method: 'POST',
        headers: { ...auth, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'MIGRATE' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || 'Could not start migration'); return; }
      toast.success('Migration started');
      setDialogOpen(false);
      setTyped('');
      await loadStatus();
    } catch (e) {
      toast.error(`Network error: ${e.message}`);
    } finally {
      setStarting(false);
    }
  };

  const pct = job?.total_collections
    ? Math.round((job.done_collections / job.total_collections) * 100)
    : 0;

  const stateBadge = () => {
    if (!job) return null;
    const map = {
      running: ['bg-blue-100 text-blue-700 border-blue-200', 'Running'],
      completed: ['bg-emerald-100 text-emerald-700 border-emerald-200', 'Completed'],
      completed_with_mismatch: ['bg-amber-100 text-amber-700 border-amber-200', 'Completed — count mismatch'],
      failed: ['bg-red-100 text-red-700 border-red-200', 'Failed'],
      aborted: ['bg-slate-100 text-slate-600 border-slate-200', 'Aborted (stale)'],
    };
    const [cls, label] = map[job.state] || ['bg-slate-100 text-slate-600 border-slate-200', job.state];
    return <Badge variant="outline" className={cn('text-xs', cls)}>{label}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* Target config */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs font-semibold text-slate-700">Target MONGO_URL</Label>
          <Input
            type="password"
            value={mongoUrl}
            onChange={(e) => setMongoUrl(e.target.value)}
            placeholder={target?.configured ? `Saved: ${target.masked_url}` : 'mongodb+srv://user:pass@cluster…'}
            className="mt-1 font-mono text-xs"
            autoComplete="off"
          />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-700">Target DB name</Label>
          <Input
            value={dbName}
            onChange={(e) => setDbName(e.target.value)}
            placeholder="fedbd_production"
            className="mt-1 font-mono text-xs"
          />
        </div>
      </div>
      <div className="flex items-center gap-3">
        <Button size="sm" variant="outline" onClick={handleSaveTarget} disabled={saving} className="gap-1.5">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Save target (encrypted)
        </Button>
        {target?.configured && (
          <span className="text-xs text-emerald-700 flex items-center gap-1">
            <CheckCircle2 className="w-3.5 h-3.5" /> Target configured: {target.db_name}
          </span>
        )}
      </div>

      {/* Run */}
      <div className="pt-2 border-t border-slate-200">
        <Button
          variant="outline"
          size="sm"
          disabled={!target?.configured || running}
          onClick={() => { setTyped(''); setDialogOpen(true); }}
          className="border-red-300 text-red-700 hover:bg-red-50 gap-1.5"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Database className="w-4 h-4" />}
          {running ? 'Migration running…' : 'Start Migration (wipe + copy)'}
        </Button>
      </div>

      {/* Progress / results */}
      {job && (
        <div className="rounded-lg border border-slate-200 bg-white p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold text-slate-700">
              Migration → {job.target_db_name}
            </span>
            {stateBadge()}
          </div>

          {job.total_collections > 0 && (
            <>
              <div className="flex items-center gap-2 text-[11px] text-slate-500">
                <span>{job.done_collections}/{job.total_collections} collections</span>
                {job.current_collection && <span>· copying <code className="font-mono">{job.current_collection}</code></span>}
                <span className="ml-auto font-bold">{pct}%</span>
              </div>
              <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                <div className={cn('h-full rounded-full transition-all',
                  job.state === 'failed' ? 'bg-red-500'
                    : job.state === 'completed' ? 'bg-emerald-500' : 'bg-blue-500')}
                  style={{ width: `${pct}%` }} />
              </div>
            </>
          )}

          {job.error && (
            <div className="flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
              <XCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {job.error}
            </div>
          )}

          {/* Per-collection verification */}
          {(job.collections || []).length > 0 && (
            <div className="max-h-48 overflow-y-auto mt-1">
              <table className="w-full text-[11px]">
                <thead className="text-slate-400 uppercase tracking-wider">
                  <tr><th className="text-left py-1">Collection</th><th className="text-right">Source</th><th className="text-right">Target</th><th className="text-center">OK</th></tr>
                </thead>
                <tbody>
                  {job.collections.map((c) => (
                    <tr key={c.name} className="border-t border-slate-100">
                      <td className="py-0.5 font-mono text-slate-600">{c.name}</td>
                      <td className="text-right tabular-nums">{c.source_count}</td>
                      <td className="text-right tabular-nums">{c.target_count}</td>
                      <td className="text-center">
                        {c.match ? <CheckCircle2 className="w-3 h-3 text-emerald-500 inline" />
                          : <XCircle className="w-3 h-3 text-red-500 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Confirm dialog */}
      <AlertDialog open={dialogOpen} onOpenChange={(v) => { if (!starting) { setDialogOpen(v); if (!v) setTyped(''); } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Start database migration?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-3 mt-2">
                <div>
                  This copies every collection to <strong>{target?.db_name}</strong> on the target cluster.
                  Each target collection is <strong>dropped and overwritten</strong> (wipe + copy).
                </div>
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-xs">
                  The <strong>target</strong> database will be overwritten. Make sure the target is the
                  new/empty cluster — not your live source. The source is only read, never modified.
                </div>
                <div>
                  <Label className="text-xs font-semibold text-slate-700">
                    Type <code className="bg-slate-100 px-1 rounded font-mono">MIGRATE</code> to confirm:
                  </Label>
                  <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="MIGRATE"
                    className="mt-1 font-mono" autoFocus disabled={starting} />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={starting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRun} disabled={!armed || starting}
              className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-40">
              {starting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Starting…</> : 'Start migration'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
